import { Component, Fragment } from 'react';
import '../src/App.scss'
import TopMenu from './common-components/TopMenu/TopMenu';
import MyAccountMain from './my-account/MyAccountMain/MyAccountMain';
import Footer from './common-components/Footer/Footer';
import Header from './common-components/Header/Header'
import { I18nextProvider, Translation } from 'react-i18next';
import { BrowserRouter as Router, Switch, Route, Redirect } from 'react-router-dom';
import i18n from './helpers/localizationConfiguration';
import { GetUserInfo, GetConfiguration, Ping } from './helpers/helper-functions';
import Notifications from './common-components/Notifications/Notifications'
import AppInsightsErrorBoundary from "./services/Telemetry/TelemetryService";
import GeneralError from "./common-components/Error/General/GeneralError";
import { ApiErrorEvent, ErrorEventType } from './common-components/Notifications/Events';
import { SessionExpired } from './common-components/Error/SessionExpired/SessionExpired'
import { Configuration } from './types/Configuration';
import Breadcrumbs from './common-components/Breadcrumbs/Breadcrumbs';
import MyCompanyMain from './my-company/MyCompanyMain/MyCompanyMain';
import RouteInfo from './RouteInfo';
import 'material-icons/iconfont/material-icons.css';
import { MainMenu } from 'dw-main-menu';
import { IMainMenuProps } from 'dw-main-menu/dist/components/main-menu/main-menu';

type State = {
    userInfo: {
        accessToken: string,
        isAdp: boolean,
    },
    configuration: Configuration,
    selectedLanguage: string,
    isLoading: boolean,
    isMobile: boolean,
    notificationsElement: any,
    isAnalyticsCookieAllowed: boolean,
    tokenIsProvided: boolean,
    showSessionExpiredErrorPage: boolean,
    logoutCheckTimerIntervalId: any,
    mainMenuProps: IMainMenuProps
}

declare global {
    interface Window {
        getTheToken(): string;
    }
}

export default class App extends Component<{}, State> {
    routes: RouteInfo[] =
        [
            {
                path: "/",
                name: "My Account",
                exact: true,
                element: () => <MyAccountMain
                    accessToken={this.state.userInfo.accessToken}
                    selectedLanguage={this.state.selectedLanguage}
                    configuration={this.state.configuration}
                    tokenIsProvided={this.state.tokenIsProvided}
                    isMobile={this.state.isMobile}
                />
            },
            {
                path: "/my-company/:companyId/:companyName",
                name: ":companyName",
                exact: true,
                element: () => <MyCompanyMain
                    accessToken={this.state.userInfo.accessToken}
                    selectedLanguage={this.state.selectedLanguage}
                    tokenIsProvided={this.state.tokenIsProvided}
                    configuration={this.state.configuration}
                    isMobile={this.state.isMobile}
                />
            },
            {
                path: "/usermanagement",
                name: "User Management",
                exact: true,
                element: () => <div>user management</div>
            }
        ]

    constructor(props: {}) {
        super(props);
        this.state = {
            userInfo: {
                accessToken: '',
                isAdp: false
            },
            configuration: new Configuration({}),
            selectedLanguage: '',
            isLoading: true,
            isMobile: true,
            notificationsElement: null,
            isAnalyticsCookieAllowed: true,
            tokenIsProvided: false,
            showSessionExpiredErrorPage: false,
            logoutCheckTimerIntervalId: null,
            mainMenuProps: {} as IMainMenuProps
        };
        this.handleResize = this.handleResize.bind(this);
        this.onLanguageChanged = this.onLanguageChanged.bind(this)
    }

    handleResize() {
        let isMobile = false;
        if (typeof window != undefined)
            isMobile = window.innerWidth < 1024;

        this.setState({ isMobile: isMobile });
    }

    async componentDidMount() {
        var userInfo = await GetUserInfo();
        var configuration = await GetConfiguration();

        this.setState({ selectedLanguage: i18n.language, configuration: configuration, userInfo: userInfo });

        window.addEventListener('resize', this.handleResize);
        this.handleResize();
        window.scrollTo({ top: 1, left: 0 })
        window.getTheToken = function () { return userInfo.accessToken };

        this.setState({ tokenIsProvided: true });

        this.handleSessionExpired();

        this.setState({ isLoading: false });

        this.runPingTimer();

        let _mainMenuProps: IMainMenuProps = {
            activeLink: "MyAccount",
            isAdp: userInfo.isAdp && configuration.shouldShowMyBusiness,
            isLoggedIn: !!userInfo.accessToken,
            myAccountLink: `${configuration.myAccountLink}?culture=${this.state.selectedLanguage}`,
            myBusinessLink: `${configuration.myBusinessLink}?culture=${this.state.selectedLanguage}`
        };

        this.setState({mainMenuProps: _mainMenuProps})
    }

    /** Renders a result for the user when boundaries errors occured. */
    renderErrorBoundary() {
        let onClick = () => window.location.reload();

        return <div className="error-boundary">
            <h1 className="error-boundary-text"><Translation>{t => t("Something went wrong")}</Translation></h1>
            <a className="error-boundary-reload" onClick={onClick} href="#"><Translation>{t => t("Click here to reload the page")}</Translation></a>
        </div>;
    }

    // every 5 seconds the get info endpoint is called for 2 purposes
    // if the cookie is expired by an external logout, then a session timeout is triggered
    // also if in the backend the token is refreshed, the getTheToken function gets a new token
    runPingTimer() {
        var interval = window.setInterval(async () => {
            var result = await Ping();
            if (result === "SESSION_EXPIRED") {
                this.setState({ showSessionExpiredErrorPage: true });
                window.clearInterval(this.state.logoutCheckTimerIntervalId);
                this.setState({ logoutCheckTimerIntervalId: null });
            }
        }, this.state.configuration.logoutCheckIntervalMs);
        this.setState({ logoutCheckTimerIntervalId: interval });

    }

    handleSessionExpired() {
        document.addEventListener('apiErrorEvent', ((event: CustomEvent<ApiErrorEvent>) => {
            if (event.detail.errorType === ErrorEventType.SessionExpired) {
                this.setState({ showSessionExpiredErrorPage: true });
            }
        }) as EventListener);
    }

    onLanguageChanged(newLanguage: string) {
        this.setState({ selectedLanguage: newLanguage });
        this.setState({ mainMenuProps: {
            ...this.state.mainMenuProps
            , myAccountLink: `${this.state.configuration.myAccountLink}?culture=${newLanguage}`
            , myBusinessLink: `${this.state.configuration.myBusinessLink}?culture=${newLanguage}`
        }});
        i18n.changeLanguage(newLanguage);
    }

    getRouteInfo(routes: any[], props: any) {
        return routes
            // Get all routes that contain the current one.
            .filter(({ path }) => props.match.path.includes(path))
            // Swap out any dynamic routes with their param values.
            // E.g. "/pizza/:pizzaId" will become "/pizza/1"
            .map(({ path, name, ...rest }) => ({
                path: Object.keys(props.match.params).length
                    ? Object.keys(props.match.params).reduce(
                        (currPath, param) => currPath.replace(
                            `:${param}`, props.match.params[param]
                        ), path
                    )
                    : path,
                // Swap the name to the param value, if requested.
                // E.g. route "/pizza/:pizzaId" with name ":pizzaId"
                // will generate a breadcrumb with the value of ":pizzaId"
                name: (name.startsWith(':'))
                    ? props.match.params[name.slice(1)]
                    : name,
                ...rest
            }));
    }

    render() {
        if (this.state.isLoading === true)
            return "";

        return (
            <I18nextProvider i18n={i18n}>
                {this.state.showSessionExpiredErrorPage
                    ?
                    <SessionExpired docuwareBaseUrl={this.state.configuration.docuwareBaseUrl} loginRedirect={this.state.configuration.loginRedirect} />
                    :
                    <AppInsightsErrorBoundary onError={GeneralError} isActive={this.state.isAnalyticsCookieAllowed}>
                        <Header />
                        <div className='dw-grid'>
                            <div className='dw-top-menu'>
                                <TopMenu
                                    onLanguageChanged={this.onLanguageChanged}
                                    configuration={this.state.configuration}
                                    tokenIsProvided={this.state.tokenIsProvided}
                                />
                            </div>
                            <div className="dw-main-menu">
                                <MainMenu {...this.state.mainMenuProps} />
                            </div>
                            <Router>
                                <Switch>
                                    {this.routes.map((route) => (
                                        <Route
                                            key={route.path}
                                            path={route.path}
                                            exact={route.exact}
                                            render={(props) => {
                                                var routeInfo = this.getRouteInfo(this.routes, props);
                                                return (
                                                    <Fragment>
                                                        {
                                                            (route.path !== "/")
                                                                ? <Breadcrumbs routeInfo={routeInfo} backTo={'/'} isMobile={this.state.isMobile}/>
                                                                : null
                                                        }
                                                        <route.element />
                                                    </Fragment>
                                                );
                                            }}
                                        />
                                    ))}
                                    <Redirect to="/" />
                                </Switch>
                            </Router>
                            <div className='dw-footer'>
                                <Footer selectedLanguage={this.state.selectedLanguage} configuration={this.state.configuration} />
                            </div>
                        </div>
                        <Notifications ref={(el) => this.state = { ...this.state, notificationsElement: el }} />
                    </AppInsightsErrorBoundary>
                }
            </I18nextProvider >
        )
    }
}
